import KpiCard from "./KpiCard";
import { ErrorBanner } from "./AnalyticsStates";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
} from "./analyticsFormatters";
import useAnalyticsDashboard from "hooks/useAnalyticsDashboard";
import { getLeadsKpiLabel, isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const formatKpiValue = (key, kpis) => {
  switch (key) {
    case "totalRevenue":
    case "totalExpenses":
    case "totalCommissions":
    case "netProfit":
    case "mrr":
      return formatCurrency(kpis[key]);
    case "conversionRate":
      return formatPercent(kpis.conversionRate);
    case "totalLeads":
    case "leadsThisMonth":
    case "vslOptInSubmissions":
    case "salesCount":
      return formatNumber(kpis[key]);
    case "topTrafficSource":
      return kpis.topTrafficSource ?? "-";
    default:
      return "-";
  }
};

const DEFAULT_KPI_CONFIG = [
  { key: "totalRevenue", title: "Total Revenue", icon: "DollarSign", subtitle: "Stripe revenue" },
  { key: "totalLeads", title: "Total Leads", icon: "Users", subtitle: "Leads in selected date range" },
  { key: "conversionRate", title: "Conversion Rate", icon: "TrendingUp", subtitle: "Stripe purchases ÷ VSL opt-in submissions" },
  { key: "netProfit", title: "Net Profit", icon: "PieChart", subtitle: "Profit & loss" },
  { key: "topTrafficSource", title: "Top Traffic Source", icon: "Globe", subtitle: "GA4 sessions" },
];

const OVERVIEW_KPI_CONFIG = [
  { key: "totalRevenue", title: "Revenue", icon: "DollarSign", subtitle: "Stripe revenue" },
  { key: "salesCount", title: "Sales Count", icon: "Users", subtitle: "Sales count" },
  { key: "mrr", title: "MRR", icon: "DollarSign", subtitle: "Monthly recurring revenue" },
  // { key: "totalExpenses", title: "Expenses", icon: "Receipt", subtitle: "Manual team, subscriptions & other" },
  // { key: "totalCommissions", title: "Commissions", icon: "Percent", subtitle: "Email, auto & sales commissions" },
  {
    key: "leadsThisMonth",
    title: "Leads",
    icon: "Users",
    subtitle: "GHL Forms Analytics",
  },
  // {
  //   key:"vslOptInSubmissions",
  //   title: "VSL Opt-In Submissions",
  //   icon: "Users",
  //   subtitle: "VSL opt-in submissions",
  // },
  {
    key: "netProfit",
    title: "Net Profit",
    icon: "PieChart",
    subtitle: "Revenue − expenses − commissions",
  },
  {
    key: "conversionRate",
    title: "Conversion Rate",
    icon: "TrendingUp",
    subtitle: "Stripe purchases ÷ VSL opt-in submissions",
  },
];

export default function UnifiedKpiCards({ api, filters, variant = "default" }) {
  const isOverview = variant === "overview";
  const { kpis, isLoading, error } = useAnalyticsDashboard(api, filters);
  console.log("kpis--------------------->",kpis);
  const KPI_CONFIG = isOverview ? OVERVIEW_KPI_CONFIG : DEFAULT_KPI_CONFIG;

  if (isCustomRangeIncomplete(filters) && !isLoading) {
    return (
      <p className="text-sm text-gray-500 italic mb-6">
        Select start and end dates to view KPIs.
      </p>
    );
  }

  return (
    <div className="mb-6">
      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
          isOverview ? "lg:grid-cols-3" : "lg:grid-cols-3 xl:grid-cols-5"
        }`}
      >
        {KPI_CONFIG.map(({ key, title, icon, subtitle }) => {
          const resolvedIcon =
            key === "netProfit"
              ? kpis.netProfit >= 0
                ? "TrendingUp"
                : "TrendingDown"
              : icon;
          const resolvedTitle =
            key === "leadsThisMonth"
              ? getLeadsKpiLabel(filters?.dateRangeType)
              : key === "netProfit"
                ? kpis.netProfit >= 0
                  ? "Net Profit"
                  : "Net Loss"
                : title;

          return (
            <KpiCard
              key={key}
              title={resolvedTitle}
              value={formatKpiValue(key, kpis)}
              icon={resolvedIcon}
              subtitle={subtitle}
              isLoading={isLoading}
            />
          );
        })}
      </div>
    </div>
  );
}
